/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { DuplicateReportInput } from '@futrli/views/mutations/reportActions';
import { LiveReportList } from '@futrli-sdk/predict';
import { WizardFormErrors } from '@futrli/ui-components/components/sidebar/wizard/types';

const DASHBOARD_NAME_ID = 'dashboard-name';

export type DuplicateDashboardRequestData = Omit<DuplicateReportInput, 'reportId'>;

export const useDuplicateDashboardForm = (dashboard: LiveReportList | undefined) => {
  const [errors, setErrors] = useState<WizardFormErrors>({});
  const [name, setName] = useState('');

  const intl = useIntl();
  const hasBeenValidated = useRef(false);

  // Initialise form data from dashboard if provided
  useEffect(() => {
    if (dashboard) {
      setName(
        intl.formatMessage(
          {
            id: 'predict.dashboards.duplicate_dashboard_name.copy_of',
            defaultMessage: '[Copy] {name}'
          },
          { name: dashboard.name }
        )
      );
    }
  }, [dashboard, intl]);

  const validate = useCallback(() => {
    const newErrors: WizardFormErrors = {};

    if (!name.trim().length) {
      newErrors.name = {
        id: DASHBOARD_NAME_ID,
        message: intl.formatMessage({
          id: 'predict.dashboards.validation.dashboard_name_is_required',
          defaultMessage: 'Dashboard name is required'
        })
      };
    }

    setErrors(newErrors);
    hasBeenValidated.current = true;
    return Object.values(newErrors).length === 0;
  }, [intl, name]);

  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  const onChangeName = useCallback((e: any) => {
    setName(e.target.value);
  }, []);

  const getRequestData = useCallback((): DuplicateDashboardRequestData | undefined => {
    if (dashboard !== undefined) {
      return {
        reportName: name
      };
    }
    return undefined;
  }, [dashboard, name]);

  return {
    errors,
    name,
    getRequestData,
    onChangeName,
    revalidate,
    validate
  };
};
